import { useEffect, useMemo, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { cafeteriaClient } from "../apiClient/cafeteriaClient";
import { ApiError } from "../../platform/apiClient/apiRequest";
import { formatEgp, parseEgpInput } from "../utils/money";
import { MonthlySummary } from "../components/MonthlySummary";
import type { CafeteriaCouponStatus, CafeteriaMenuItem, CafeteriaWallet } from "../types/cafeteria.types";

const SCANNER_ELEMENT_ID = "cafeteria-qr-reader";

// A scanned code could be either a wallet's own qrToken (charge it) or a coupon's own
// qrToken (redeem it, free) — they're unrelated random tokens, so after every scan we ask
// the coupon endpoint whether it recognizes the code; if not, we fall back to charging it
// as a wallet. One scanner, no separate "mode" for staff to think about.
type ScanPhase =
  | { kind: "idle" }
  | { kind: "resolving" }
  | { kind: "coupon"; token: string; status: CafeteriaCouponStatus }
  | { kind: "charge"; token: string };

type ScanResult =
  | { kind: "charge_success"; wallet: CafeteriaWallet; charged: number }
  | { kind: "coupon_success"; wallet: CafeteriaWallet; itemName: string }
  | { kind: "insufficient" | "not_found" | "expired" | "already_redeemed" | "error"; message: string };

export function CafeteriaCounterPage() {
  const [menu, setMenu] = useState<CafeteriaMenuItem[]>([]);
  const [menuError, setMenuError] = useState<string | null>(null);
  const [isLoadingMenu, setIsLoadingMenu] = useState(true);
  const [showSummary, setShowSummary] = useState(false);
  const [menuQuery, setMenuQuery] = useState("");
  const [menuCategoryFilter, setMenuCategoryFilter] = useState("");
  // Collapsed by default so staff can jump straight to the scan button instead of scrolling
  // past every category — searching/filtering bypasses this and just shows what matched.
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const [cart, setCart] = useState<Map<string, number>>(new Map());
  const [scanning, setScanning] = useState(false);
  const [phase, setPhase] = useState<ScanPhase>({ kind: "idle" });
  const [partialMode, setPartialMode] = useState(false);
  const [partialAmountText, setPartialAmountText] = useState("");
  const [chargeInputError, setChargeInputError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ScanResult | null>(null);

  const scannerRef = useRef<Html5Qrcode | null>(null);

  function getScanner(): Html5Qrcode {
    if (!scannerRef.current) {
      scannerRef.current = new Html5Qrcode(SCANNER_ELEMENT_ID);
    }
    return scannerRef.current;
  }

  useEffect(() => {
    void cafeteriaClient
      .getMenu()
      .then(setMenu)
      .catch((err) => setMenuError(err instanceof Error ? err.message : "Failed to load the menu."))
      .finally(() => setIsLoadingMenu(false));
  }, []);

  useEffect(() => {
    return () => {
      void scannerRef.current?.stop().catch(() => {});
    };
  }, []);

  const cartEntries = [...cart.entries()];
  const total = cartEntries.reduce((sum, [id, qty]) => {
    const item = menu.find((m) => m.id === id);
    return sum + (item ? item.price * qty : 0);
  }, 0);
  // A coupon covers exactly one item — only usable when the staff already narrowed the
  // order down to that, so we never ask them to re-pick what they just picked.
  const singleItemId = cartEntries.length === 1 && cartEntries[0][1] === 1 ? cartEntries[0][0] : null;
  const singleItem = singleItemId ? menu.find((m) => m.id === singleItemId) : undefined;

  const menuCategories = useMemo(() => [...new Set(menu.map((i) => i.category).filter(Boolean))].sort(), [menu]);

  const filteredMenu = useMemo(() => {
    const q = menuQuery.trim().toLowerCase();
    return menu.filter((item) => {
      if (menuCategoryFilter && item.category !== menuCategoryFilter) return false;
      if (!q) return true;
      return item.name.toLowerCase().includes(q) || item.category.toLowerCase().includes(q);
    });
  }, [menu, menuQuery, menuCategoryFilter]);

  // The menu already comes back sorted category-then-name, so consecutive runs of the
  // same category can just be grouped in a single pass.
  const groupedMenu = useMemo(() => {
    const out: { category: string; items: CafeteriaMenuItem[] }[] = [];
    for (const item of filteredMenu) {
      const last = out[out.length - 1];
      if (last && last.category === item.category) last.items.push(item);
      else out.push({ category: item.category, items: [item] });
    }
    return out;
  }, [filteredMenu]);

  // Searching or filtering already narrows down to what matched — collapsing those results
  // behind a second click would defeat the point, so show them open regardless of toggle state.
  const isSearchActive = menuQuery.trim() !== "" || menuCategoryFilter !== "";

  function toggleCategory(category: string) {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }

  function addItem(id: string) {
    setCart((prev) => {
      const next = new Map(prev);
      next.set(id, (next.get(id) ?? 0) + 1);
      return next;
    });
  }

  function removeItem(id: string) {
    setCart((prev) => {
      const next = new Map(prev);
      const qty = next.get(id) ?? 0;
      if (qty <= 1) next.delete(id);
      else next.set(id, qty - 1);
      return next;
    });
  }

  function resetScan() {
    setPhase({ kind: "idle" });
    setPartialMode(false);
    setPartialAmountText("");
    setChargeInputError(null);
  }

  async function resolveScannedToken(token: string) {
    setPhase({ kind: "resolving" });
    try {
      const status = await cafeteriaClient.getCouponStatusForToken(token);
      setPhase(status.available ? { kind: "coupon", token, status } : { kind: "charge", token });
    } catch {
      // Coupon lookup failing (network hiccup, etc.) shouldn't block a payment — fall back
      // to treating it as a wallet charge; a genuinely bad code will 404 there too.
      setPhase({ kind: "charge", token });
    }
  }

  async function submitCharge(amountEgp?: number) {
    if (phase.kind !== "charge") return;
    const token = phase.token;
    setSubmitting(true);
    setChargeInputError(null);
    try {
      const items = [...cart.entries()].map(([menuItemId, quantity]) => ({ menuItemId, quantity }));
      const charged = amountEgp ?? total;
      const wallet = await cafeteriaClient.charge({ qrToken: token, items, amountEgp });
      setResult({ kind: "charge_success", wallet, charged });
      resetScan();
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        setResult({ kind: "insufficient", message: err.message });
      } else if (err instanceof ApiError && err.status === 404) {
        setResult({ kind: "not_found", message: err.message });
      } else {
        setResult({ kind: "error", message: err instanceof Error ? err.message : "Couldn't charge this order." });
      }
      resetScan();
    } finally {
      setSubmitting(false);
    }
  }

  function handleConfirmPartial() {
    const amount = parseEgpInput(partialAmountText);
    if (amount === null || amount <= 0) {
      setChargeInputError("Enter a valid amount, e.g. 30.00.");
      return;
    }
    if (amount > total) {
      setChargeInputError(`Can't charge more than the order total (${formatEgp(total)}).`);
      return;
    }
    void submitCharge(amount);
  }

  async function handleConfirmCouponRedeem(itemId: string) {
    if (phase.kind !== "coupon") return;
    const item = menu.find((m) => m.id === itemId);
    if (!item) return;
    const token = phase.token;
    setSubmitting(true);
    try {
      const wallet = await cafeteriaClient.redeemCoupon(token, itemId);
      setResult({ kind: "coupon_success", wallet, itemName: item.name });
      resetScan();
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setResult({ kind: "not_found", message: err.message });
      } else if (err instanceof ApiError && err.status === 410) {
        setResult({ kind: "expired", message: err.message });
      } else if (err instanceof ApiError && err.status === 409) {
        setResult({ kind: "already_redeemed", message: err.message });
      } else {
        setResult({ kind: "error", message: err instanceof Error ? err.message : "Couldn't redeem this coupon." });
      }
      resetScan();
    } finally {
      setSubmitting(false);
    }
  }

  async function startScan() {
    setResult(null);
    setScanning(true);
    const scanner = getScanner();
    try {
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: 250 },
        (decodedText) => {
          void scanner
            .stop()
            .catch(() => {})
            .finally(() => {
              setScanning(false);
              void resolveScannedToken(decodedText);
            });
        },
        () => {
          // per-frame decode miss — expected constantly while aiming the camera, ignore
        },
      );
    } catch {
      setScanning(false);
      setResult({ kind: "error", message: "Couldn't access the camera. Check camera permissions and try again." });
    }
  }

  async function cancelScan() {
    await scannerRef.current?.stop().catch(() => {});
    setScanning(false);
  }

  function newOrder() {
    setCart(new Map());
    setResult(null);
    resetScan();
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Cafeteria counter</h1>
          <p className="page-sub">Pick the items, then scan the customer's code — it works for both payment and free-meal coupons.</p>
        </div>
        <div className="page-header-actions">
          <button type="button" className="btn btn-ghost" onClick={() => setShowSummary((s) => !s)}>
            {showSummary ? "Hide monthly summary" : "View monthly summary"}
          </button>
        </div>
      </header>

      {showSummary && <MonthlySummary />}

      {menuError ? (
        <div className="state-panel">
          <p>{menuError}</p>
        </div>
      ) : isLoadingMenu ? (
        <div className="state-panel">Loading…</div>
      ) : (
        <div className="counter-layout">
          <div className="cafeteria-menu-picker">
            {menu.length === 0 ? (
              <div className="state-panel">
                <p>No active menu items yet.</p>
              </div>
            ) : (
              <>
                <div className="cafeteria-menu-filters">
                  <input
                    type="text"
                    className="coach-search"
                    placeholder="Search by name or category…"
                    value={menuQuery}
                    onChange={(e) => setMenuQuery(e.target.value)}
                    aria-label="Search menu items"
                  />
                  <select
                    className="coach-search"
                    value={menuCategoryFilter}
                    onChange={(e) => setMenuCategoryFilter(e.target.value)}
                    aria-label="Filter by category"
                  >
                    <option value="">All categories</option>
                    {menuCategories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                {groupedMenu.length === 0 ? (
                  <div className="state-panel">
                    <p>No items match.</p>
                  </div>
                ) : (
                  groupedMenu.map((group) => {
                    const inCartCount = group.items.reduce((sum, item) => sum + (cart.get(item.id) ?? 0), 0);
                    const isOpen = isSearchActive || expandedCategories.has(group.category);
                    return (
                      <div key={group.category} className="cafeteria-menu-category">
                        <button
                          type="button"
                          className="cafeteria-menu-category-toggle"
                          onClick={() => toggleCategory(group.category)}
                          aria-expanded={isOpen}
                        >
                          <span className="cafeteria-menu-category-toggle-label">
                            <span className="cafeteria-menu-category-name">{group.category || "Uncategorized"}</span>
                            <span className="cafeteria-menu-category-count">
                              ({group.items.length}){inCartCount > 0 && ` · ${inCartCount} in order`}
                            </span>
                          </span>
                          <span className={isOpen ? "cafeteria-menu-category-chevron is-open" : "cafeteria-menu-category-chevron"}>▾</span>
                        </button>
                        {isOpen &&
                          group.items.map((item) => {
                            const qty = cart.get(item.id) ?? 0;
                            return (
                              <div key={item.id} className="cafeteria-menu-picker-row">
                                <span className="cafeteria-menu-picker-name">{item.name}</span>
                                <span className="cafeteria-menu-picker-price">{formatEgp(item.price)}</span>
                                <div className="cafeteria-qty-control">
                                  <button
                                    type="button"
                                    className="btn btn-ghost"
                                    onClick={() => removeItem(item.id)}
                                    disabled={qty === 0}
                                  >
                                    −
                                  </button>
                                  <span className="cafeteria-qty-value">{qty}</span>
                                  <button type="button" className="btn btn-ghost" onClick={() => addItem(item.id)}>
                                    +
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    );
                  })
                )}
              </>
            )}
          </div>

          <div className="cafeteria-cart">
            <h2>Order</h2>
            {cart.size === 0 ? (
              <p className="cafeteria-cart-empty">Add items from the menu.</p>
            ) : (
              <ul className="cafeteria-cart-items">
                {[...cart.entries()].map(([id, qty]) => {
                  const item = menu.find((m) => m.id === id);
                  if (!item) return null;
                  return (
                    <li key={id} className="cafeteria-cart-item">
                      <span>
                        {qty} × {item.name}
                      </span>
                      <span>{formatEgp(item.price * qty)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="cafeteria-cart-total">
              <span>Total</span>
              <span>{formatEgp(total)}</span>
            </div>

            {result?.kind === "charge_success" && (
              <div className="cafeteria-scan-result cafeteria-scan-result--success">
                <p>
                  Charged {formatEgp(result.charged)}. New balance: {formatEgp(result.wallet.balance)}.
                </p>
                <button type="button" className="btn btn-primary" onClick={newOrder}>
                  New order
                </button>
              </div>
            )}
            {result?.kind === "coupon_success" && (
              <div className="cafeteria-scan-result cafeteria-scan-result--success">
                <p>Free meal redeemed — {result.itemName}. Nothing charged.</p>
                <button type="button" className="btn btn-primary" onClick={newOrder}>
                  New order
                </button>
              </div>
            )}
            {result?.kind === "insufficient" && (
              <div className="cafeteria-scan-result cafeteria-scan-result--error">
                <p>{result.message}</p>
                <button type="button" className="btn btn-ghost" onClick={() => setResult(null)}>
                  Try another code
                </button>
              </div>
            )}
            {result?.kind === "not_found" && (
              <div className="cafeteria-scan-result cafeteria-scan-result--warning">
                <p>{result.message}</p>
                <button type="button" className="btn btn-ghost" onClick={() => setResult(null)}>
                  Try again
                </button>
              </div>
            )}
            {(result?.kind === "expired" || result?.kind === "already_redeemed") && (
              <div className="cafeteria-scan-result cafeteria-scan-result--error">
                <p>{result.message}</p>
                <button type="button" className="btn btn-ghost" onClick={() => setResult(null)}>
                  Dismiss
                </button>
              </div>
            )}
            {result?.kind === "error" && (
              <div className="cafeteria-scan-result cafeteria-scan-result--error">
                <p>{result.message}</p>
                <button type="button" className="btn btn-ghost" onClick={() => setResult(null)}>
                  Dismiss
                </button>
              </div>
            )}

            {!result && phase.kind === "resolving" && <div className="state-panel">Checking code…</div>}

            {!result && phase.kind === "coupon" && (
              <div className="cafeteria-charge-confirm">
                <p className="field-hint">
                  🎟️ {phase.status.description}
                  {phase.status.expiresAt && <> — expires {phase.status.expiresAt}</>}
                </p>
                {singleItem ? (
                  <>
                    <p className="field-hint">Redeem free meal — {singleItem.name}?</p>
                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => void handleConfirmCouponRedeem(singleItem.id)}
                        disabled={submitting}
                      >
                        {submitting ? "Redeeming…" : "Redeem free meal"}
                      </button>
                      <button type="button" className="btn btn-danger-ghost" onClick={resetScan} disabled={submitting}>
                        Cancel — rescan
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="field-hint">
                      {cart.size === 0
                        ? "Add the item they're getting, then scan again — a coupon covers exactly one item."
                        : "A coupon covers exactly one item — clear the order down to just that one, then scan again."}
                    </p>
                    <button type="button" className="btn btn-ghost" onClick={resetScan}>
                      Try again
                    </button>
                  </>
                )}
              </div>
            )}

            {!result && phase.kind === "charge" && cart.size === 0 && (
              <div className="cafeteria-scan-result cafeteria-scan-result--warning">
                <p>No items selected — add items to the order, then scan again.</p>
                <button type="button" className="btn btn-ghost" onClick={resetScan}>
                  Try again
                </button>
              </div>
            )}

            {!result && phase.kind === "charge" && cart.size > 0 && (
              <div className="cafeteria-charge-confirm">
                <p className="field-hint">Code scanned. Charge the full order, or a partial amount?</p>
                {!partialMode ? (
                  <>
                    <div className="form-actions">
                      <button type="button" className="btn btn-primary" onClick={() => void submitCharge()} disabled={submitting}>
                        {submitting ? "Charging…" : `Charge full amount (${formatEgp(total)})`}
                      </button>
                      <button type="button" className="btn btn-ghost" onClick={() => setPartialMode(true)} disabled={submitting}>
                        Charge partial amount
                      </button>
                    </div>
                    <button type="button" className="btn btn-danger-ghost" onClick={resetScan} disabled={submitting}>
                      Cancel — rescan
                    </button>
                  </>
                ) : (
                  <div className="form">
                    <div className="form-field">
                      <label>Amount to charge (EGP)</label>
                      <input
                        type="text"
                        placeholder={`up to ${total.toFixed(2)}`}
                        value={partialAmountText}
                        onChange={(e) => setPartialAmountText(e.target.value)}
                        disabled={submitting}
                        autoFocus
                      />
                    </div>
                    {chargeInputError && <div className="form-banner form-banner--error">{chargeInputError}</div>}
                    <div className="form-actions">
                      <button type="button" className="btn btn-primary" onClick={handleConfirmPartial} disabled={submitting}>
                        {submitting ? "Charging…" : "Confirm charge"}
                      </button>
                      <button type="button" className="btn btn-ghost" onClick={() => setPartialMode(false)} disabled={submitting}>
                        Back
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!result && phase.kind === "idle" && (
              <>
                <div id={SCANNER_ELEMENT_ID} className={scanning ? "cafeteria-scan-panel is-active" : "cafeteria-scan-panel"} />
                {scanning ? (
                  <button type="button" className="btn btn-ghost" onClick={cancelScan}>
                    Cancel scan
                  </button>
                ) : (
                  <button type="button" className="btn btn-primary" onClick={startScan}>
                    Scan customer's code
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
